import { FC, useMemo, useEffect } from 'react'
import * as styles from 'components/features/socialFeed/ComposeDecoration/ComposeDecoration.styl'
import { ComposeMentionsSuggests } from 'components/features/socialFeed/ComposeMentionsSuggests/ComposeMentionsSuggests'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import {
  suggestUsersActionCreator,
  composeSetInputBehaviorActionCreator,
  feedComposeFetchLinkActionCreator,
  suggestTagsActionCreator,
} from 'actions/feedComposeActions'
import { ComposeId, COMPOSE_SUGGEST_INPUT, COMPOSE_DEFAULT_INPUT } from 'interfaces/feed'
import { createComposePostUidSelector } from 'selectors/feedComposeSelectors'
import { Decoration } from 'helpers/composeHelpers'
import { ComposeTagsSuggests } from 'components/features/socialFeed/ComposeTagsSuggests/ComposeTagsSuggests'
import { useComposeState } from 'helpers/hookHelpers'

type ComposeDecorationProps = {
  composeId: ComposeId
  decoration: Decoration
  isSelected: boolean
  text: string
}

export const ComposeDecoration: FC<ComposeDecorationProps> = (props) => {
  const {
    composeId,
    decoration,
    isSelected,
    text,
  } = props

  const {
    start: decorationStart,
    end: decorationEnd,
    type: decorationType,
  } = decoration

  const {
    decorationText,
    tagText,
    mentionText,
    linkText,
    hasSuggest,
  } = useMemo(() => {
    const subText = text.substring(decorationStart, decorationEnd)
    const contText = subText.substr(1)
    const tag = decorationType === 'tag' ? contText : null
    const mention = decorationType === 'mention' ? contText : null

    return {
      decorationText: subText,
      tagText: tag,
      mentionText: mention,
      linkText: decorationType === 'link' ? subText : null,
      hasSuggest: Boolean(tag !== null || mention !== null),
    }
  }, [decorationStart, decorationEnd, text, decorationType])

  const { isFocused: isComposeFocused } = useComposeState(composeId, { isFocused: false })

  const dispatch = useStoreDispatch()

  const composePostUidSelector = useMemo(createComposePostUidSelector, [])
  const postUid = useStoreSelector((state) => composePostUidSelector(state, props))

  const mentionNeedSuggest = mentionText !== null
  const tagNeedSuggest = tagText !== null && tagText.length > 0

  useEffect(() => {
    if (!isSelected || !isComposeFocused) {
      return
    }

    if (mentionText && mentionNeedSuggest) {
      dispatch(suggestUsersActionCreator.request({
        search: mentionText,
        ...(postUid ? { postUid } : {}),
      }))

    } else if (tagText && tagNeedSuggest) {
      dispatch(suggestTagsActionCreator.request({
        search: tagText,
        ...(postUid ? { postUid } : {}),
      }))

    } else if (linkText !== null) {
      dispatch(feedComposeFetchLinkActionCreator.request({
        url: linkText,
        composeId,
      }))
    }
    // mentionText, tagText, linkText - it's just substring of decorationText.
    // Thats why it doesn't make sense to add them to deps
    // postUid and composeId never changes while a component mounted
  }, [decorationType, decorationText, isSelected, isComposeFocused, mentionText, mentionNeedSuggest, tagText, tagNeedSuggest, linkText, dispatch, postUid, composeId])

  useEffect(() => {
    if (!isSelected || !hasSuggest) {
      return
    }

    dispatch(composeSetInputBehaviorActionCreator({
      composeId,
      type: COMPOSE_SUGGEST_INPUT,
    }))

    return () => {
      dispatch(composeSetInputBehaviorActionCreator({
        composeId,
        type: COMPOSE_DEFAULT_INPUT,
      }))
    }
    // composeId never changes while a component mounted
  }, [isSelected, hasSuggest, dispatch, composeId])

  return (
    <span className={ styles.composeDecoration }>
      { decorationText }
      { isComposeFocused && isSelected && mentionNeedSuggest && mentionText && (
        <span className={ styles.suggest }>
          <span className={ styles.suggestChild }>
            <ComposeMentionsSuggests
              composeId={ composeId }
              search={ mentionText }
              decoration={ decoration }
            />
          </span>
        </span>
      )}
      { isComposeFocused && isSelected && tagNeedSuggest && tagText && (
        <span className={ styles.suggest }>
          <span className={ styles.suggestChild }>
            <ComposeTagsSuggests
              composeId={ composeId }
              search={ tagText }
              decoration={ decoration }
            />
          </span>
        </span>
      )}
    </span>
  )
}
